import { useState } from 'react';
import {
  Card, CardHeader, CardBody, Button, Modal, Field, Input, Select,
  Table, THead, TBody, TR, TH, TD, StatusBadge, EmptyState, ErrorNotice, Loading,
  ConfirmDialog, CopyField, CopyButton,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { formatTimestamp, formatExactTimestamp } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { Webhook, AlertTriangle } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

const EVENTS = ['payment.created', 'payment.processing', 'payment.succeeded', 'payment.failed', 'payment.refunded'];

/** WH-8's delivery states, in the order an event moves through them. */
const DELIVERY_STATUSES = [
  ['', 'Any status'], ['pending', 'Pending'], ['delivering', 'Delivering'], ['delivered', 'Delivered'],
  ['failed', 'Failed'], ['exhausted', 'Gave up'],
];

/**
 * WH-11: "The documentation must state plainly that deliveries can repeat, and
 * show a verification example the merchant can paste."
 *
 * This snippet is the same algorithm the dispatcher runs, so what a merchant
 * copies is what we actually send — not a paraphrase that drifts.
 */
const VERIFICATION_SNIPPET = `const crypto = require('crypto');

// Verify over the RAW body, before any JSON parsing — re-serialising changes
// the bytes and a valid signature will fail.
app.post('/webhooks', express.raw({ type: 'application/json' }), (req, res) => {
  const header = req.get('Distrx-Signature') || '';
  const parts = Object.fromEntries(header.split(',').map((p) => p.split('=')));

  const expected = crypto
    .createHmac('sha256', process.env.DISTRXPAY_WEBHOOK_SECRET)
    .update(\`\${parts.t}.\${req.body}\`)
    .digest('hex');

  const ok = parts.v1
    && parts.v1.length === expected.length
    && crypto.timingSafeEqual(Buffer.from(parts.v1), Buffer.from(expected));

  // Reject anything older than five minutes, or a captured request can be replayed.
  const fresh = Math.abs(Date.now() / 1000 - Number(parts.t)) < 300;
  if (!ok || !fresh) return res.status(400).send('invalid signature');

  const event = JSON.parse(req.body);

  // Deliveries CAN repeat: a timeout after your server committed looks exactly
  // like a failure on ours, so we retry. Key off Distrx-Event-Id and make
  // handling idempotent.
  if (alreadyProcessed(req.get('Distrx-Event-Id'))) return res.sendStatus(200);

  handle(event);
  res.sendStatus(200);
});`;

function EventRow({ event, onRetry, retrying }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TR>
        <TD>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex items-center gap-1.5 text-left font-mono text-xs text-accent-ink hover:underline"
          >
            <svg viewBox="0 0 20 20" className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="currentColor" aria-hidden="true">
              <path d="M7 4l6 6-6 6V4z" />
            </svg>
            {event.eventType}
          </button>
        </TD>
        <TD><StatusBadge status={event.status} size="sm" /></TD>
        <TD align="right" className="tabular text-xs">{event.attempts} / {event.maxAttempts}</TD>
        <TD align="right" className="text-xs text-muted">{formatTimestamp(event.createdAt)}</TD>
        <TD align="right">
          {/* WH-8: any event can be retried by hand. */}
          {event.status !== 'delivered' && (
            <Button size="sm" variant="ghost" loading={retrying} onClick={() => onRetry(event)}>Retry</Button>
          )}
        </TD>
      </TR>

      {expanded && (
        <TR>
          <TD colSpan={5} className="bg-surface-2">
            <div className="space-y-4 py-2">
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Payload</p>
                <pre className="max-h-64 overflow-auto rounded-md border border-line bg-white p-3 text-[11px] leading-relaxed text-ink-soft">
                  {JSON.stringify(event.payload, null, 2)}
                </pre>
              </div>

              {/* WH-8: every attempt, with response status, body snippet, error and duration. */}
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                  Delivery attempts ({event.deliveries?.length ?? 0})
                </p>
                {event.deliveries?.length ? (
                  <div className="divide-y divide-line-soft rounded-md border border-line bg-white">
                    {event.deliveries.map((attempt) => (
                      <div key={attempt.attempt} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-2.5 text-xs">
                        <span className="font-medium text-ink-soft">#{attempt.attempt}</span>
                        <span className={attempt.responseStatus >= 200 && attempt.responseStatus < 300 ? 'text-emerald-600' : 'text-red-600'}>
                          {attempt.responseStatus ?? 'no response'}
                        </span>
                        <span className="text-muted">{formatExactTimestamp(attempt.at)}</span>
                        {attempt.durationMs != null && <span className="tabular text-muted">{attempt.durationMs}ms</span>}
                        {attempt.error && <span className="w-full text-red-600">{attempt.error}</span>}
                        {attempt.responseBody && (
                          <span className="w-full truncate font-mono text-muted">{attempt.responseBody}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted">Not attempted yet.</p>
                )}
              </div>
            </div>
          </TD>
        </TR>
      )}
    </>
  );
}

export function WebhooksPage() {
  const queue = useResource('/developers/webhooks/queue');
  const toast = useToast();
  const { refreshMerchant } = useAuth();
  const endpoints = useResource('/developers/webhooks/endpoints');
  const [eventStatus, setEventStatus] = useState('');
  const events = useResource('/developers/webhooks/events', {
    params: { limit: 25, ...(eventStatus ? { status: eventStatus } : {}) },
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ url: '', description: '', enabledEvents: EVENTS });
  const [issuedSecret, setIssuedSecret] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const create = useMutation(async () => {
    const result = await http.post('/developers/webhooks/endpoints', {
      url: form.url,
      ...(form.description ? { description: form.description } : {}),
      enabledEvents: form.enabledEvents,
    });
    setIssuedSecret(result.signingSecret);
    setCreateOpen(false);
    setForm({ url: '', description: '', enabledEvents: EVENTS });
    endpoints.refetch();
    refreshMerchant();
  });

  const remove = useMutation(async (endpoint) => {
    await http.delete(`/developers/webhooks/endpoints/${endpoint.id}`);
    toast.success('Endpoint deleted');
    setDeleting(null);
    endpoints.refetch();
  });

  const rotate = useMutation(async (endpoint) => {
    const result = await http.post(`/developers/webhooks/endpoints/${endpoint.id}/rotate-secret`);
    setIssuedSecret(result.signingSecret);
    endpoints.refetch();
  });

  const retry = useMutation(async (event) => {
    await http.post(`/developers/webhooks/events/${event.id}/retry`);
    toast.success('Queued for redelivery');
    events.refetch();
  });

  const toggleEvent = (name) =>
    setForm((f) => ({
      ...f,
      enabledEvents: f.enabledEvents.includes(name)
        ? f.enabledEvents.filter((e) => e !== name)
        : [...f.enabledEvents, name],
    }));

  return (
    <>
      <PageHero
        icon={Webhook}
        chip="Developers"
        title="Webhooks"
        blurb="We tell your server what happened, and keep trying until it acknowledges."
        actions={<Button onClick={() => setCreateOpen(true)}>Add endpoint</Button>}
      />

      {/*
        * The dispatcher is a process a merchant cannot see. When it is not
        * running every payment still completes and nobody is ever told — orders
        * sit unfulfilled, and the customer's own checkout page says "confirming
        * your payment" indefinitely. Saying so here is the difference between a
        * two-minute fix and an afternoon.
        */}
      {queue.data?.stalled && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">Nothing is delivering your webhooks</p>
            <p className="mt-1 text-sm leading-relaxed text-amber-800">
              {queue.data.dueNow} event{queue.data.dueNow === 1 ? ' is' : 's are'} due and none have been
              collected. Payments are still being taken — but your server is not being told about them,
              so orders will sit unfulfilled.
            </p>
            <p className="mt-1.5 text-xs text-amber-800">
              If you are running this yourself, the API was started with{' '}
              <code className="rounded bg-amber-100 px-1.5 py-0.5 font-mono">RUN_JOBS=false</code>{' '}
              and nothing is running <code className="rounded bg-amber-100 px-1.5 py-0.5 font-mono">npm run jobs</code>.
            </p>
          </div>
        </div>
      )}

      <Card className="mb-6">
        <CardHeader title="Your endpoints" />
        {endpoints.loading && <Loading label="Loading endpoints…" />}
        {endpoints.error && <ErrorNotice error={endpoints.error} onRetry={endpoints.refetch} className="m-4" />}

        {!endpoints.loading && !endpoints.error && endpoints.data?.length === 0 && (
          <EmptyState
            title="No webhook endpoints yet"
            blurb="Without one, your server won't be told when a payment succeeds — the return redirect alone is not reliable enough to mark an order paid."
            action={<Button onClick={() => setCreateOpen(true)}>Add your first endpoint</Button>}
          />
        )}

        {!endpoints.loading && !endpoints.error && endpoints.data?.length > 0 && (
          <div className="divide-y divide-line-soft">
            {endpoints.data.map((endpoint) => (
              <div key={endpoint.id} className="flex flex-wrap items-start justify-between gap-3 p-5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-mono text-sm text-ink">{endpoint.url}</span>
                    <StatusBadge status={endpoint.status} size="sm" />
                    {endpoint.managedBy === 'api_key' && (
                      <span
                        className="rounded-full border border-line px-2 py-0.5 text-[11px] font-medium text-muted"
                        title="An integration created this endpoint with its API key and manages it. Revoking that key removes it."
                      >
                        {endpoint.description || 'Integration'} · managed by its API key
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    {endpoint.enabledEvents.length} event{endpoint.enabledEvents.length === 1 ? '' : 's'}
                    {' · '}secret {endpoint.signingSecretHint}
                    {endpoint.lastSuccessAt && ` · last delivered ${formatTimestamp(endpoint.lastSuccessAt)}`}
                  </p>
                  {/* WH-9 */}
                  {endpoint.status === 'disabled' && endpoint.disabledReason && (
                    <p className="mt-1.5 text-xs text-red-600">{endpoint.disabledReason}</p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" loading={rotate.loading} onClick={() => rotate.mutate(endpoint).catch(() => {})}>
                    Rotate secret
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(endpoint)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mb-6">
        <CardHeader
          title="Verifying a webhook"
          blurb="Paste this into your server. It is the same algorithm we sign with."
          actions={<CopyButton value={VERIFICATION_SNIPPET} label="Copy snippet" />}
        />
        <CardBody>
          <pre className="max-h-96 overflow-auto rounded-md border border-line bg-slate-900 p-4 text-xs leading-relaxed text-slate-100">
            <code>{VERIFICATION_SNIPPET}</code>
          </pre>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Recent events"
          blurb="Expand a row to see the payload and every delivery attempt"
          actions={
            <Field>
              {(props) => (
                <Select {...props} aria-label="Filter by delivery status" value={eventStatus}
                  onChange={(e) => setEventStatus(e.target.value)}>
                  {DELIVERY_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </Select>
              )}
            </Field>
          }
        />
        {events.loading && <Loading label="Loading events…" />}
        {events.error && <ErrorNotice error={events.error} onRetry={events.refetch} className="m-4" />}

        {!events.loading && !events.error && events.data?.events?.length === 0 && (
          eventStatus ? (
            <EmptyState
              title="No events with that status"
              blurb="Pick another status, or show them all."
              action={<Button variant="secondary" onClick={() => setEventStatus('')}>Show all events</Button>}
            />
          ) : (
            <EmptyState
              title="No events yet"
              blurb="Events appear here as soon as you take a payment, with every delivery attempt we made."
            />
          )
        )}

        {!events.loading && !events.error && events.data?.events?.length > 0 && (
          <Table>
            <THead>
              <TR><TH>Event</TH><TH>Status</TH><TH align="right">Attempts</TH><TH align="right">Created</TH><TH align="right">Actions</TH></TR>
            </THead>
            <TBody>
              {events.data.events.map((event) => (
                <EventRow key={event.id} event={event} retrying={retry.loading} onRetry={(e) => retry.mutate(e).catch(() => {})} />
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add a webhook endpoint"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={create.loading}>Cancel</Button>
            <Button onClick={() => create.mutate().catch(() => {})} loading={create.loading}>Add endpoint</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate().catch(() => {}); }}>
          {create.error && <ErrorNotice error={create.error} title="Could not add the endpoint" />}

          <Field label="Endpoint URL" hint="Must be reachable from the internet and respond within 10 seconds." required error={create.error?.fieldErrors?.url}>
            {(props) => (
              <Input {...props} type="url" inputMode="url" value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://yourstore.com/webhooks/distrxpay" autoFocus />
            )}
          </Field>

          <Field label="Description">
            {(props) => <Input {...props} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Production order fulfilment" />}
          </Field>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink">Events to send</legend>
            <div className="space-y-1.5">
              {EVENTS.map((name) => (
                <label key={name} className="flex cursor-pointer items-center gap-2.5 text-sm text-ink-soft">
                  <input
                    type="checkbox"
                    checked={form.enabledEvents.includes(name)}
                    onChange={() => toggleEvent(name)}
                    className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent"
                  />
                  <span className="font-mono text-xs">{name}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </form>
      </Modal>

      {/* WH-5: shown once, like an API key. */}
      <Modal
        open={Boolean(issuedSecret)}
        onClose={() => setIssuedSecret(null)}
        title="Copy your signing secret"
        blurb="This is the only time it will be shown."
        footer={<Button onClick={() => setIssuedSecret(null)}>I&rsquo;ve saved it</Button>}
      >
        <div className="space-y-4">
          <CopyField label="Signing secret" value={issuedSecret} />
          <p className="text-xs leading-relaxed text-ink-soft">
            Use it to verify the <code className="font-mono">Distrx-Signature</code> header on every delivery.
            An unverified webhook endpoint will accept anything anyone sends it.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting).catch(() => {})}
        loading={remove.loading}
        title="Delete this endpoint?"
        consequence="We will stop sending events to this URL immediately, including any that are queued or waiting to retry. Your server will no longer be told when payments succeed."
        confirmLabel="Delete endpoint"
      />
    </>
  );
}

export default WebhooksPage;
